import type { ModelContextLike, ModelContextTool, RegisteredTool, ToolResult } from './adapter'

// Le polyfill est un `Window` distinct de celui du navigateur : ces deux
// propriétés n'existent QUE quand `ensureWebMCP()` a posé le secours local.
// Les déclarer ici (plutôt qu'avec un `as any`) donne à `window.__LOCAL_WEBMCP__`
// et `window.callWebMCPTool` de vrais types partout où ils sont lus — console
// de développement comprise, via les fichiers `.d.ts` générés.
declare global {
  interface Window {
    __LOCAL_WEBMCP__?: LocalWebMCPContext
    callWebMCPTool?: (name: string, input?: Record<string, unknown>) => Promise<ToolResult>
  }
}

export class LocalWebMCPContext extends EventTarget implements ModelContextLike {
  // Seule marque qui distingue ce polyfill d'un vrai `document.modelContext`
  // natif : `checkAvailability()` (adapter.ts) la lit pour que l'écran sache
  // qu'aucun agent WebMCP réel n'a répondu, même quand l'enregistrement des
  // outils a parfaitement réussi contre ce secours local.
  readonly isLocalPolyfill = true as const
  readonly tools = new Map<string, ModelContextTool>()

  registerTool = async (
    tool: ModelContextTool,
    options?: { signal?: AbortSignal },
  ): Promise<void> => {
    if (options?.signal?.aborted) {
      throw options.signal.reason instanceof Error
        ? options.signal.reason
        : new DOMException('aborted', 'AbortError')
    }
    this.tools.set(tool.name, tool)
    options?.signal?.addEventListener('abort', () => {
      this.tools.delete(tool.name)
      this.dispatchEvent(new Event('toolchange'))
    })
    this.dispatchEvent(new Event('toolchange'))
  }

  getTools = async (): Promise<RegisteredTool[]> => {
    return [...this.tools.values()].map((t) => ({
      name: t.name,
      title: t.title,
      description: t.description,
      inputSchema: t.inputSchema,
      annotations: t.annotations,
    }))
  }

  executeTool = async (name: string, input: Record<string, unknown> = {}): Promise<ToolResult> => {
    const tool = this.tools.get(name)
    if (!tool) {
      return {
        content: [
          { type: 'text', text: `Tool "${name}" is not registered or currently unavailable.` },
        ],
        isError: true,
      }
    }
    return tool.execute(input, { signal: new AbortController().signal })
  }
}

export function ensureWebMCP(): void {
  // Never polyfill in test mode so Vitest tests run against clean test harness
  if (import.meta.env.MODE === 'test') return
  if (typeof document === 'undefined') return

  // Keep native browser implementation if available
  if (document.modelContext?.registerTool || globalThis.navigator?.modelContext?.registerTool) {
    return
  }

  const polyfill = new LocalWebMCPContext()
  document.modelContext = polyfill
  window.__LOCAL_WEBMCP__ = polyfill

  // Expose handy helper in developer console
  window.callWebMCPTool = (name: string, input?: Record<string, unknown>) => {
    return polyfill.executeTool(name, input ?? {})
  }
}
